import { useState } from "react"
import { cn, formatAmount } from "@/lib/utils"
import { redirectToLk } from "@/config/lk"
import { CreditCardIcon, SbpIcon } from "@/components/icons"
import { PaymentMethodIcons } from "@/components/payments/payment-method-icons"
import {
  SherpaDialog,
  SherpaDialogContent,
  SherpaDialogDescription,
  SherpaDialogFooter,
  SherpaDialogHeader,
  SherpaDialogTitle,
} from "@/components/ui-kit/dialog"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useContractMutations } from "@/hooks/contracts/useContracts"
import { usePaymentMutations, usePaymentOptions } from "@/hooks/payments/usePayments"
import { buildPaymentReturnUrl } from "@/lib/payments-api"
import { followPaymentConfirmation } from "@/lib/payment-confirm"
import { getPaymentFailureMessage } from "@/lib/payment-messages"
import type { ApiError } from "@/types/api"
import type { Invoice } from "@/types/invoice"
import type { PaymentGateway, PaymentMethod, PaymentMethodType, PaymentOption } from "@/types/payment"
import { ContractsDialogSuccess } from "@/components/contracts/modals/contracts-dialog-success"
import { ArrowRightIcon, Loader2, XCircle } from "lucide-react"

function formatCardMethodTitle(title: string): string {
  const last4 = title.replace(/\D/g, "").slice(-4)

  if (last4.length === 4) {
    return `•••• ${last4}`
  }

  return title
}

function getPaymentErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined
  }

  const apiError = error as ApiError

  if (typeof apiError.standardizedCode === "string") {
    return apiError.standardizedCode
  }

  const detailsCode = apiError.details?.code
  return typeof detailsCode === "string" ? detailsCode : undefined
}

function ContractsDialogPaymentError({
  errorCode,
  onBack,
}: {
  errorCode?: string
  onBack: () => void
}) {
  return (
    <>
      <div className="flex justify-center pt-[40px] mb-6 sm:mb-8">
        <XCircle className="size-20 text-[#df0f0f]" />
      </div>
      <SherpaDialogHeader className="mb-8 sm:mb-[87px]">
        <SherpaDialogTitle className="text-center w-full block text-[28px] leading-[36px] sm:text-[48px] sm:leading-[52px]">
          Оплата не прошла
        </SherpaDialogTitle>
        <SherpaDialogDescription className="grow text-center text-lg leading-6">
          {getPaymentFailureMessage(errorCode)}
        </SherpaDialogDescription>
      </SherpaDialogHeader>
      <SherpaDialogFooter className="flex-col gap-4 sm:flex-col sm:justify-start pt-[10px]">
        <UiKitButton type="button" className="h-[60px] w-full" onClick={onBack}>
          Вернуться к оплате
        </UiKitButton>
      </SherpaDialogFooter>
    </>
  )
}

function ContractsDialogPending() {
  return (
    <>
      <div className="flex justify-center pt-[40px] mb-6 sm:mb-8">
        <Loader2 className="size-16 animate-spin text-brand" />
      </div>
      <SherpaDialogHeader className="mb-8 sm:mb-[87px]">
        <SherpaDialogTitle className="text-center w-full block text-[28px] leading-[36px] sm:text-[48px] sm:leading-[52px]">
          Оформляем оплату
        </SherpaDialogTitle>
        <SherpaDialogDescription className="grow text-center text-lg leading-6">
          Пожалуйста, подождите — это займёт несколько секунд
        </SherpaDialogDescription>
      </SherpaDialogHeader>
    </>
  )
}

function getPaymentOptionKey(option: PaymentOption): string {
  return `${option.gateway}-${option.type}`
}

function NewPaymentMethodOption({
  option,
  disabled,
}: {
  option: PaymentOption
  disabled: boolean
}) {
  const itemId = `new-pay-method-${getPaymentOptionKey(option)}`

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border-2 border-transparent bg-muted px-4 py-3 transition-colors",
        "hover:border-brand/50 has-[[data-state=checked]]:border-brand has-[[data-state=checked]]:bg-card",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <RadioGroupItem
        id={itemId}
        value={getPaymentOptionKey(option)}
        className="size-4 border-[#818796] bg-white data-checked:border-brand data-checked:bg-brand dark:data-checked:bg-brand [&_[data-slot=radio-group-indicator]_span]:bg-white"
      />
      <Label htmlFor={itemId} className="flex flex-1 cursor-pointer items-center gap-3 font-normal justify-end">
        {option.type === "qr" ? (
          <>
            <span className="text-base leading-[22px] text-foreground">{option.title}</span>
            <SbpIcon size={20} />
          </>
        ) : (
          <>
            <span className="text-base leading-[22px] text-foreground">{option.title}</span>
            <PaymentMethodIcons />
          </>
        )}
      </Label>
    </div>
  )
}

function SavedPaymentMethodOption({
  method,
  disabled,
}: {
  method: PaymentMethod
  disabled: boolean
}) {
  const itemId = `pay-method-${method.id}`

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border-2 border-transparent bg-muted px-4 py-3 transition-colors",
        "hover:border-brand/50 has-[[data-state=checked]]:border-brand has-[[data-state=checked]]:bg-card",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <RadioGroupItem
        id={itemId}
        value={method.id}
        className="size-4 border-[#818796] bg-white data-checked:border-brand data-checked:bg-brand dark:data-checked:bg-brand [&_[data-slot=radio-group-indicator]_span]:bg-white"
      />
      <Label htmlFor={itemId} className="flex flex-1 cursor-pointer items-center gap-3 font-normal justify-end">
        {method.type === "qr" ? (
          <>
            <span className="text-base leading-[22px] text-foreground">{method.title}</span>
            <SbpIcon size={20} />
          </>
        ) : (
          <>
            <span className="text-base leading-[22px] tracking-[0.08em] text-foreground">
              {formatCardMethodTitle(method.title)}
            </span>
            <CreditCardIcon size={20} />
          </>
        )}
      </Label>
    </div>
  )
}

function AutoPaymentToggle({
  contractId,
  enabled,
  onEnabledChange,
}: {
  contractId: number
  enabled: boolean
  onEnabledChange: (enabled: boolean) => void
}) {
  const { enableRecurrent, disableRecurrent } = useContractMutations()
  const isRecurrentPending = enableRecurrent.isPending || disableRecurrent.isPending

  const handleAutoPaymentChange = async (nextEnabled: boolean) => {
    if (isRecurrentPending || nextEnabled === enabled) {
      return
    }

    onEnabledChange(nextEnabled)

    try {
      if (nextEnabled) {
        await enableRecurrent.mutateAsync(contractId)
      } else {
        await disableRecurrent.mutateAsync(contractId)
      }
    } catch {
      onEnabledChange(!nextEnabled)
    }
  }

  const segmentClassName = (active: boolean) =>
    cn(
      "flex w-full items-center justify-center rounded px-4 py-2 text-base leading-6 transition-shadow",
      active
        ? "bg-white text-[#2b2e34] shadow-[0_2px_5px_rgba(0,0,0,0.2)]"
        : "text-[#2b2e34b3]",
    )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h4 className="text-lg leading-[26px] text-[#2b2e34]">Автоплатеж</h4>
        <SherpaDialogDescription className="max-w-[382px]">
          Подключите его и оплата будет списываться автоматически каждый месяц
        </SherpaDialogDescription>
      </div>
      <div
        className={cn(
          "grid w-full grid-cols-2 gap-1 self-start rounded-lg bg-muted p-1",
          isRecurrentPending && "pointer-events-none opacity-60",
        )}
        role="group"
        aria-label="Автоплатеж"
      >
        <button
          type="button"
          className={segmentClassName(enabled)}
          aria-pressed={enabled}
          disabled={isRecurrentPending}
          onClick={() => void handleAutoPaymentChange(true)}
        >
          Вкл.
        </button>
        <button
          type="button"
          className={segmentClassName(!enabled)}
          aria-pressed={!enabled}
          disabled={isRecurrentPending}
          onClick={() => void handleAutoPaymentChange(false)}
        >
          Выкл.
        </button>
      </div>
    </div>
  )
}

type FundingSource =
  | { methodId: string }
  | { gateway: PaymentGateway; methodType: PaymentMethodType }

function ContractsPayDialogBody({
  invoices,
  contractId,
  isRecurrent,
  showAutoPayment,
  onClose,
}: {
  invoices: Invoice[]
  contractId?: number
  isRecurrent: boolean
  showAutoPayment: boolean
  onClose: () => void
}) {
  const totalAmount = invoices.reduce((sum, invoice) => sum + invoice.amount, 0)
  const paymentLabel = invoices.length === 1 ? invoices[0].name : "К оплате"

  const [autoPaymentEnabled, setAutoPaymentEnabled] = useState(isRecurrent)
  const [isSuccess, setIsSuccess] = useState(false)
  const [hasPaymentError, setHasPaymentError] = useState(false)
  const [paymentErrorCode, setPaymentErrorCode] = useState<string | undefined>(undefined)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [selectedMethodId, setSelectedMethodId] = useState<string | undefined>(undefined)
  const [isChoosingNewMethod, setIsChoosingNewMethod] = useState(false)
  const [selectedNewOptionKey, setSelectedNewOptionKey] = useState<string | undefined>(undefined)

  const { data: paymentOptions, isLoading: isOptionsLoading, refetch: refetchOptions } = usePaymentOptions(true)
  const { createPayment } = usePaymentMutations()
  const savedMethods = paymentOptions?.savedMethods?.filter(method => method.status === "active") ?? []
  const newPaymentOptions = paymentOptions?.options ?? []
  const hasSavedMethods = savedMethods.length > 0
  const hasNewPaymentOptions = newPaymentOptions.length > 0
  const defaultMethodId = savedMethods.find(method => method.isDefault)?.id ?? savedMethods[0]?.id ?? ""
  const defaultNewOptionKey = newPaymentOptions[0] ? getPaymentOptionKey(newPaymentOptions[0]) : ""
  const resolvedMethodId =
    selectedMethodId != null && savedMethods.some(method => method.id === selectedMethodId)
      ? selectedMethodId
      : defaultMethodId
  const resolvedNewOptionKey =
    selectedNewOptionKey != null && newPaymentOptions.some(option => getPaymentOptionKey(option) === selectedNewOptionKey)
      ? selectedNewOptionKey
      : defaultNewOptionKey
  const selectedNewPaymentOption = newPaymentOptions.find(
    option => getPaymentOptionKey(option) === resolvedNewOptionKey,
  )

  const handlePay = async (funding: FundingSource) => {
    if (isProcessingPayment || invoices.length === 0) {
      return
    }

    const isSavedCardPayment = "methodId" in funding

    setHasPaymentError(false)
    setPaymentErrorCode(undefined)
    setIsProcessingPayment(true)

    try {
      const result = await createPayment.mutateAsync({
        invoiceIds: invoices.map(invoice => invoice.id),
        returnUrl: buildPaymentReturnUrl(),
        ...("methodId" in funding
          ? { methodId: funding.methodId }
          : {
              gateway: funding.gateway,
              methodType: funding.methodType,
              save: autoPaymentEnabled,
            }),
      })

      if (result.status === "succeeded") {
        if (isSavedCardPayment) {
          redirectToLk(`/payments/done?paymentId=${result.paymentId}`)
          onClose()
          return
        }

        setIsSuccess(true)
        return
      }

      if (
        isSavedCardPayment &&
        (result.status === "failed" || result.status === "canceled")
      ) {
        setPaymentErrorCode(undefined)
        setHasPaymentError(true)
        setIsProcessingPayment(false)
        return
      }

      if (result.confirmUrl) {
        followPaymentConfirmation(result)
        return
      }
    } catch (error) {
      const apiError = error as ApiError
      if (apiError.code === "409") {
        void refetchOptions()
      }

      if (isSavedCardPayment) {
        setPaymentErrorCode(getPaymentErrorCode(error))
        setHasPaymentError(true)
        setIsProcessingPayment(false)
        return
      }
    }

    setIsProcessingPayment(false)
  }

  const handleNewMethodClick = () => {
    if (newPaymentOptions.length === 1) {
      const option = newPaymentOptions[0]
      void handlePay({ gateway: option.gateway, methodType: option.type })
      return
    }

    setIsChoosingNewMethod(true)
  }

  const handlePayWithNewMethod = () => {
    if (!selectedNewPaymentOption) {
      return
    }

    void handlePay({
      gateway: selectedNewPaymentOption.gateway,
      methodType: selectedNewPaymentOption.type,
    })
  }

  if (hasPaymentError) {
    return (
      <ContractsDialogPaymentError
        errorCode={paymentErrorCode}
        onBack={() => {
          setHasPaymentError(false)
          setPaymentErrorCode(undefined)
        }}
      />
    )
  }

  if (isSuccess) {
    return (
      <ContractsDialogSuccess
        title="Оплата прошла успешно"
        description="Счёт оплачен. Обновление статуса может занять несколько секунд."
        onClose={onClose}
      />
    )
  }

  if (isProcessingPayment) {
    return <ContractsDialogPending />
  }

  return (
    <>
      <SherpaDialogHeader className="gap-6">
        <SherpaDialogTitle>Оплатить бокс</SherpaDialogTitle>

        {showAutoPayment && contractId != null && (
          <AutoPaymentToggle
            contractId={contractId}
            enabled={autoPaymentEnabled}
            onEnabledChange={setAutoPaymentEnabled}
          />
        )}
      </SherpaDialogHeader>

      <SherpaDialogFooter className="flex-col items-end gap-3 sm:gap-4 sm:flex-col sm:justify-start">
        <div className="flex w-full items-center justify-between gap-4">
          <span className="text-base leading-[22px] text-[#818796] sm:text-lg sm:leading-[26px]">{paymentLabel}</span>
          <span className="text-right text-base leading-[22px] text-[#2b2e34] sm:text-lg sm:leading-[26px] whitespace-nowrap">{formatAmount(totalAmount)}</span>
        </div>

        {isOptionsLoading ? (
          <div className="flex w-full items-center justify-center py-4 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : isChoosingNewMethod ? (
          <>
            <button
              type="button"
              className="self-start text-base leading-[22px] text-muted-foreground transition-colors hover:text-brand"
              onClick={() => setIsChoosingNewMethod(false)}
            >
              ← Назад
            </button>

            <RadioGroup
              value={resolvedNewOptionKey}
              onValueChange={setSelectedNewOptionKey}
              className="flex w-full flex-col gap-2"
            >
              {newPaymentOptions.map(option => (
                <NewPaymentMethodOption key={getPaymentOptionKey(option)} option={option} disabled={false} />
              ))}
            </RadioGroup>

            <UiKitButton
              type="button"
              className="w-full items-center"
              disabled={!selectedNewPaymentOption}
              onClick={handlePayWithNewMethod}
              endIcon={<ArrowRightIcon size={24} />}
            >
              Оплатить
            </UiKitButton>
          </>
        ) : (
          <>
            {hasSavedMethods && (
              <RadioGroup
                value={resolvedMethodId}
                onValueChange={setSelectedMethodId}
                className="w-full gap-2 grid grid-cols-2 sm:grid-cols-1"
              >
                {savedMethods.map(method => (
                  <SavedPaymentMethodOption key={method.id} method={method} disabled={false} />
                ))}
              </RadioGroup>
            )}

            {hasSavedMethods ? (
              <>
                <UiKitButton
                  type="button"
                  className="w-full items-center text-[16px] leading-[22px] sm:text-[18px] sm:leading-[24px]"
                  disabled={!resolvedMethodId}
                  onClick={() => void handlePay({ methodId: resolvedMethodId })}
                  endIcon={<ArrowRightIcon size={24} />}
                >
                  Оплатить сохранённым способом
                </UiKitButton>

                {hasNewPaymentOptions && (
                  <UiKitButton
                    type="button"
                    variant="secondary"
                    className="w-full text-[16px] leading-[22px] sm:text-[18px] sm:leading-[24px]"
                    endIcon={<PaymentMethodIcons />}
                    onClick={handleNewMethodClick}
                  >
                    Оплатить новой картой
                  </UiKitButton>
                )}
              </>
            ) : (
              <>
                {newPaymentOptions.map(option => (
                  <UiKitButton
                    key={getPaymentOptionKey(option)}
                    type="button"
                    className="w-full text-[16px] leading-[22px] sm:text-[18px] sm:leading-[24px]"
                    variant={option.type === "card" ? "primary" : "secondary"}
                    endIcon={option.type === "qr" ? <SbpIcon /> : <PaymentMethodIcons />}
                    onClick={() => void handlePay({ gateway: option.gateway, methodType: option.type })}
                  >
                    {option.title}
                  </UiKitButton>
                ))}
              </>
            )}
          </>
        )}
      </SherpaDialogFooter>
    </>
  )
}

export function ContractsPayDialog({
  open,
  onOpenChange,
  invoices,
  contractId,
  isRecurrent = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoices: Invoice[]
  contractId?: number
  isRecurrent?: boolean
}) {
  const isGroup = invoices.length > 1
  const hasFee = invoices.some(invoice => invoice.entityType === "fee")
  const showAutoPayment = contractId != null && (!isGroup || !hasFee)
  const sessionKey = `${contractId ?? "none"}-${isRecurrent}-${invoices.map(invoice => invoice.id).join(",")}`

  return (
    <SherpaDialog open={open} onOpenChange={onOpenChange}>
      <SherpaDialogContent className="h-auto max-h-[calc(100dvh-2rem)] [--sherpa-dialog-height:auto] max-sm:left-0 max-sm:h-auto max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0">
        {open && invoices.length > 0 ? (
          <ContractsPayDialogBody
            key={sessionKey}
            invoices={invoices}
            contractId={contractId}
            isRecurrent={isRecurrent}
            showAutoPayment={showAutoPayment}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SherpaDialogContent>
    </SherpaDialog>
  )
}
